import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../utils/api';
import { X, Calendar } from 'lucide-react';
import toast from 'react-hot-toast';
import { format } from 'date-fns';

function addMinutesToTime(hhmm, minutes) {
  const [h, m] = String(hhmm || '09:00')
    .split(':')
    .map((n) => parseInt(n, 10) || 0);
  const total = h * 60 + m + Math.max(15, parseInt(minutes, 10) || 60);
  const hh = String(Math.floor(total / 60) % 24).padStart(2, '0');
  const mm = String(total % 60).padStart(2, '0');
  return `${hh}:${mm}`;
}

/**
 * Schedule visit for a job — date + duration (minutes) → start/end for worker calendar.
 * Status becomes SCHEDULED (list row colour follows job.status via getJobRowStyle).
 */
function ScheduleJobModal({ job, onClose }) {
  const queryClient = useQueryClient();
  const today = format(new Date(), 'yyyy-MM-dd');
  const [scheduledDate, setScheduledDate] = useState(
    job?.scheduled_date ? format(new Date(job.scheduled_date), 'yyyy-MM-dd') : today
  );
  const [startTime, setStartTime] = useState(() => {
    if (job?.scheduled_start_time) {
      const t = String(job.scheduled_start_time);
      const m = t.match(/T(\d{2}:\d{2})/) || t.match(/^(\d{2}:\d{2})/);
      if (m) return m[1];
    }
    return '09:00';
  });
  const [durationMin, setDurationMin] = useState(() => {
    if (job?.scheduled_start_time && job?.scheduled_end_time) {
      try {
        const a = new Date(job.scheduled_start_time).getTime();
        const b = new Date(job.scheduled_end_time).getTime();
        if (Number.isFinite(a) && Number.isFinite(b) && b > a) {
          return String(Math.round((b - a) / 60000));
        }
      } catch (_) {
        /* ignore */
      }
    }
    return '60';
  });

  const endTime = useMemo(
    () => addMinutesToTime(startTime, durationMin),
    [startTime, durationMin]
  );

  const scheduleMutation = useMutation({
    mutationFn: async () => {
      const start = `${scheduledDate}T${startTime}:00`;
      const end = `${scheduledDate}T${endTime}:00`;
      const response = await api.patch(`/jobs/${job.id}`, {
        status: 'SCHEDULED',
        scheduled_date: scheduledDate,
        scheduled_start_time: start,
        scheduled_end_time: end,
      });
      return response.data;
    },
    onSuccess: (data) => {
      if (data?.schedule_shift_note) {
        toast.success(data.schedule_shift_note);
      } else {
        toast.success('Scheduled — status SCHEDULED; worker calendar updated.');
      }
      queryClient.invalidateQueries(['job', job.id]);
      queryClient.invalidateQueries(['jobs']);
      queryClient.invalidateQueries(['schedules']);
      queryClient.invalidateQueries(['building-inspections']);
      onClose();
    },
    onError: (error) => {
      const msg = error.response?.data?.error || 'Failed to schedule inspection';
      const conflict = error.response?.data?.conflict;
      toast.error(
        conflict?.title
          ? `${msg}`
          : msg
      );
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    scheduleMutation.mutate();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-card rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-bold text-gray-900 flex items-center">
            <Calendar className="w-5 h-5 mr-2 text-primary-600" />
            Schedule Inspection
          </h2>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <p className="text-sm text-gray-600">
            Set visit date and duration for <strong>{job?.ss_code}</strong>. Status becomes{' '}
            <strong>Scheduled</strong>
            {job?.assigned_worker_id
              ? '. If the worker already has that slot, the system moves to the next free time the same day.'
              : '. Assign a worker first if you need it on their calendar.'}
          </p>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Inspection Date *</label>
            <input
              type="date"
              required
              value={scheduledDate}
              onChange={(e) => setScheduledDate(e.target.value)}
              min={job?.status === 'SCHEDULED' ? undefined : today}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Start Time</label>
              <input
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Duration (minutes) *
              </label>
              <input
                type="number"
                min={15}
                step={15}
                required
                value={durationMin}
                onChange={(e) => setDurationMin(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
              <p className="text-xs text-gray-500 mt-1">Ends at {endTime}</p>
            </div>
          </div>

          <div className="flex items-center justify-end space-x-4 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={scheduleMutation.isLoading}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
            >
              {scheduleMutation.isLoading ? 'Scheduling...' : 'OK — Schedule'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ScheduleJobModal;
